import { ClipboardCopyIcon } from '@radix-ui/react-icons';
import { Badge, BadgeProps, Flex, IconButton } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSwitchInput } from 'components/common/form/switch';
import { CommonTableHoC } from 'components/common/table';
import { CommonTooltip } from 'components/common/tooltip';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { t } from 'i18next';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { generateModelKeys, getModelKey } from 'lib_ts/classes/ms.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { ModelStatus } from 'lib_ts/enums/machine-models.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminMachineModelsService } from 'services/admin/machine-models.service';
import useMiscStore from 'stores/use-misc-store';

interface IBallTypeRow {
  _id: BallType;
}

const BALL_TYPES: IBallTypeRow[] = Object.values(BallType).map((b) => ({
  _id: b,
}));

interface IProps {
  machine: Partial<IMachine>;
  updateMetadata: (model: Partial<IMachine>) => void;
  onSave: () => void;
  isNew?: boolean;
}

interface IModelOption extends IOption {
  calibrationOnly?: boolean;
}

export const BallTypesTab = (props: IProps) => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  // load models rather than depend on models context
  const [models, setModels] = useState<IMachineModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const updating = useMachinesPortalStore((s) => s.updating);

  useEffect(() => {
    const loadModels = async () => {
      setLoadingModels(true);

      const result =
        await AdminMachineModelsService.getInstance().getAllModels();

      setModels(result);
      setLoadingModels(false);
    };

    loadModels();
  }, []);

  const activeKey = useMemo(() => getModelKey(props.machine), [props.machine]);
  const validKeys = Object.keys(generateModelKeys());

  /** for model_id:
   * undefined => deleting an existing key
   * '' (empty string) => adding a new key
   * (anything else) => updating a key
   *
   * for model_key: value will be trimmed before setting dictionary key-value pair
   */
  const onModelChange = useCallback(
    (key: string, id: string | undefined) => {
      const dict: { [key: string]: string } = { ...props.machine.model_ids };

      if (id === undefined) {
        /** delete the key (if it exists) */
        delete dict[key];
        props.updateMetadata({ model_ids: dict });
        return;
      }

      // check if key is allowed
      const safeModelKey = (key || '').trim();
      if (!safeModelKey) {
        NotifyHelper.error({ message_md: 'Invalid or empty model key.' });
        return;
      }

      /** add or update the key */
      dict[safeModelKey] = id;
      props.updateMetadata({ model_ids: dict });
    },
    [props.machine, props.updateMetadata]
  );

  // { [BallType]: IModelOption[] }
  const modelOptionsDict = useMemo(() => {
    const output: { [key: string]: IModelOption[] } = {};

    if (!props.machine.model_ids) {
      return output;
    }

    // ignore draft models
    const activeModels = models.filter(
      (m) =>
        m.status === ModelStatus.Published ||
        Object.values(props.machine.model_ids || {}).includes(m._id)
    );

    Object.values(BallType).forEach((key) => {
      const value = props.machine.model_ids?.[key];

      const sortedModels = activeModels
        .filter((m) => {
          if (m._id === value) {
            // currently used model
            return true;
          }

          if (m.archived) {
            // exclude archived models
            return false;
          }

          if (!m.machineID) {
            // allowed for any machine
            return true;
          }

          if (m.machineID === props.machine.machineID) {
            // allowed for this machine
            return true;
          }

          return false;
        })
        .sort((a, b) => StringHelper.safeSort(a.name, b.name));

      const options: IOption[] = [];

      options.push(
        ...sortedModels
          .filter((m) => m.calibration_only)
          .map((m) => {
            const o: IModelOption = {
              group: 'Calibration Models',
              value: m._id,
              label: m.name,
              calibrationOnly: m.calibration_only,
            };
            return o;
          })
      );

      options.push(
        ...sortedModels
          .filter((m) => !m.calibration_only)
          .filter((m) => !m.machineID)
          .map((m) => {
            const o: IModelOption = {
              group: 'Generic Models',
              value: m._id,
              label: m.name,
              calibrationOnly: m.calibration_only,
            };
            return o;
          })
      );

      options.push(
        ...sortedModels
          .filter((m) => !m.calibration_only)
          .filter((m) => m.machineID === props.machine.machineID)
          .map((m) => {
            const o: IModelOption = {
              group: 'Machine Models',
              value: m._id,
              label: m.name,
              calibrationOnly: m.calibration_only,
            };
            return o;
          })
      );

      output[key] = options;
    });

    return output;
  }, [props.machine, models]);

  const toggleBallType = (ballType: BallType, checked: boolean) => {
    const ballTypeOptions = props.machine.ball_type_options || [BallType.MLB];
    const newBallTypes = checked
      ? [...ballTypeOptions, ballType]
      : ballTypeOptions.filter((t) => t !== ballType);

    props.updateMetadata({
      ball_type_options: newBallTypes,
    });
  };

  const columns = useMemo<ITypedTableColumn<IBallTypeRow>[]>(
    () => [
      {
        label: 'common.ball-type',
        key: '_ball_type',
        formatFn: (b) => {
          const isActive = activeKey === b._id;
          const isValid = validKeys.includes(b._id);
          const isAvailable = props.machine.ball_type_options?.includes(b._id);

          const assignedModel = (props.machine.model_ids ?? {})[b._id];
          const needsModel = isAvailable && !assignedModel;

          const { badgeColor, tooltip } = (() => {
            const tooltipLabels = [];
            let color: BadgeProps['color'];

            if (isActive) {
              color = RADIX.COLOR.SUCCESS;
              tooltipLabels.push('Active');
            }

            if (!isActive) {
              color = RADIX.COLOR.NEUTRAL;
              tooltipLabels.push('Inactive');
            }

            if (!isValid) {
              color = RADIX.COLOR.DANGER;
              tooltipLabels.push('Invalid key');
            }

            if (needsModel) {
              color = RADIX.COLOR.DANGER;
              tooltipLabels.push('No assigned model');
            }

            const assignedIsCalibrationOnly = modelOptionsDict[b._id].find(
              ({ value }) => value === assignedModel
            )?.calibrationOnly;

            // warn the user if the assigned model is for ball type calibration
            if (assignedIsCalibrationOnly) {
              color = RADIX.COLOR.WARNING;
              tooltipLabels.push('Assigned model is for ball type calibration');
            }

            return {
              badgeColor: color,
              tooltip: tooltipLabels.join('  \n'),
            };
          })();

          return (
            <CommonTooltip
              trigger={<Badge color={badgeColor}>{b._id}</Badge>}
              text_md={tooltip}
            />
          );
        },
      },
      {
        label: 'admin.available',
        key: '_available',
        formatFn: (b) => (
          <CommonSwitchInput
            checked={props.machine.ball_type_options?.includes(b._id)}
            id={`${b._id}-available-switch`}
            onCheckedChange={(v) => toggleBallType(b._id, v)}
          />
        ),
      },
      {
        label: 'admin.model-id',
        key: '_model_id',
        formatFn: (b) => {
          const value = (props.machine.model_ids ?? {})[b._id];
          const values = value ? [value] : [];

          return (
            <CommonSearchInput
              id="machine-editor-model"
              name="model_id"
              options={modelOptionsDict[b._id]}
              values={values}
              onChange={(v) => onModelChange(b._id, v[0])}
              disabled={loadingModels || updating}
              optional
            />
          );
        },
      },
      {
        label: '',
        key: '_copy_model_id',
        formatFn: (b) => {
          const modelID = (props.machine.model_ids ?? {})[b._id];

          return (
            <IconButton
              title={t('common.copy-to-clipboard').toString()}
              variant="soft"
              disabled={!modelID}
              onClick={() => {
                const model = modelOptionsDict[b._id].find(
                  ({ value }) => value === modelID
                );

                if (!model) {
                  NotifyHelper.warning({
                    message_md: `No model found for model ID \`${modelID}\`.`,
                  });
                  return;
                }

                const value = `${model.label} | ${model.value}`;
                navigator.clipboard.writeText(value).then(() =>
                  NotifyHelper.success({
                    message_md: t('common.x-copied-to-clipboard', {
                      x: `\`${value}\``,
                    }),
                  })
                );
              }}
            >
              <ClipboardCopyIcon />
            </IconButton>
          );
        },
      },
    ],
    [
      props.machine.model_ids,
      props.machine.ball_type_options,
      loadingModels,
      updating,
      modelOptionsDict,
    ]
  );

  // matches the Save button rendered in MachineEditor except for an additional disabled prop toggled when models are loading
  const saveButtonProps = {
    label: props.isNew ? 'Create' : 'Save Changes',
    color: props.isNew ? RADIX.COLOR.SUCCESS : RADIX.COLOR.ACCENT,
    loading: updating,
    disabled: loadingModels,
    variant: RADIX.BUTTON.VARIANT.PRIMARY,
    onClick: props.onSave,
  };

  return (
    <ErrorBoundary identifier="MachinesTable">
      <CommonTableHoC
        dataTestID="AdminBallTypes"
        identifier="AdminBallTypesTable"
        data-testid="AdminBallTypes"
        displayColumns={columns}
        enableListener={!anyOpenDialogs}
        loading={loadingModels}
        displayData={BALL_TYPES}
      />
      <Flex gap={RADIX.FLEX.GAP.SM} justify="end" mt="5">
        <DialogButton
          label={saveButtonProps.label}
          color={saveButtonProps.color}
          loading={saveButtonProps.loading}
          onClick={saveButtonProps.onClick}
          variant={saveButtonProps.variant}
        />
      </Flex>
    </ErrorBoundary>
  );
};
